'use client'

import { PlusCircle, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EnumSelect } from '@/components/ui/enum-select'
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field'
import type { CustomerAddressType, HelmCommunicationChannel } from '@/lib/api/types'
import { TextAreaField, TextField, formatLabel } from '@/src/components/wizard/fields'
import {
  blankAddress,
  blankContact,
  splitList,
  type AddressDraft,
  type ContactDraft,
} from './customer-edit-errors'

const CHANNELS: HelmCommunicationChannel[] = ['EMAIL', 'WHATSAPP', 'BOTH', 'NONE']
const ADDRESS_TYPES: CustomerAddressType[] = [
  'BILLING',
  'SHIPPING',
  'PICKUP',
  'DELIVERY',
  'CONSIGNEE',
  'NOTIFY_PARTY',
  'OTHER',
]
const options = (values: readonly string[]) =>
  values.map((value) => ({ value, label: formatLabel(value) }))

/** The other entries of a list whose first entry is shown in its own field. */
const others = (values: string[], primary: string | null | undefined) =>
  values.filter((value) => value !== (primary ?? '').trim())

interface ListEditorProps<T> {
  rows: T[]
  onChange: (rows: T[]) => void
  errors: Record<string, string>
  onClearError: (key: string) => void
}

/**
 * The customer's contacts: add, edit, remove. One is the main contact. Editing
 * a contact never changes the customer's own email or phone; changing those on
 * the Details tab updates the main contact too.
 */
export function ContactsEditor({
  rows,
  onChange,
  errors,
  onClearError,
}: ListEditorProps<ContactDraft>) {
  const update = (index: number, patch: Partial<ContactDraft>, field?: string) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)))
    if (field) onClearError(`contact-${index}-${field}`)
  }
  const makeMain = (index: number) =>
    onChange(rows.map((row, rowIndex) => ({ ...row, is_primary: rowIndex === index })))
  const remove = (index: number) => {
    const next = rows.filter((_row, rowIndex) => rowIndex !== index)
    onChange(
      next.length > 0 && !next.some((row) => row.is_primary)
        ? next.map((row, rowIndex) => ({ ...row, is_primary: rowIndex === 0 }))
        : next,
    )
  }

  return (
    <FieldGroup>
      {rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No contact persons recorded.</p>
      )}
      {rows.map((contact, index) => {
        const id = (field: string) => `contact-${index}-${field}`
        return (
          <FieldSet key={index} className="rounded-md border p-3" data-testid={`contact-${index}`}>
            <div className="flex items-center justify-between gap-3">
              <FieldLegend variant="label" className="mb-0 flex items-center gap-2">
                {contact.name.trim() || `Contact ${index + 1}`}
                {contact.is_primary && <Badge variant="secondary">Main contact</Badge>}
              </FieldLegend>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => remove(index)}
                aria-label={`Remove contact ${index + 1}`}
              >
                <Trash2 />
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id={id('name')}
                label="Name"
                required
                value={contact.name}
                onChange={(value) => update(index, { name: value }, 'name')}
                error={errors[id('name')]}
              />
              <TextField
                id={id('title')}
                label="Role"
                value={contact.title ?? ''}
                onChange={(value) => update(index, { title: value })}
                placeholder="Finance manager"
              />
              <TextField
                id={id('email')}
                label="Email"
                inputMode="email"
                value={contact.primary_email ?? ''}
                onChange={(value) =>
                  update(
                    index,
                    {
                      primary_email: value,
                      emails: [
                        value.trim(),
                        ...others(contact.emails, contact.primary_email),
                      ].filter(Boolean),
                    },
                    'email',
                  )
                }
                error={errors[id('email')]}
              />
              <TextField
                id={id('emails')}
                label="Other emails"
                value={others(contact.emails, contact.primary_email).join(', ')}
                onChange={(value) =>
                  update(
                    index,
                    {
                      emails: [contact.primary_email?.trim() ?? '', ...splitList(value)].filter(
                        Boolean,
                      ),
                    },
                    'emails',
                  )
                }
                error={errors[id('emails')]}
                placeholder="Comma separated"
              />
              <TextField
                id={id('phone')}
                label="Phone"
                type="tel"
                value={contact.primary_phone ?? ''}
                onChange={(value) =>
                  update(index, {
                    primary_phone: value,
                    phones: [value.trim(), ...others(contact.phones, contact.primary_phone)].filter(
                      Boolean,
                    ),
                  })
                }
              />
              <TextField
                id={id('phones')}
                label="Other phones"
                value={others(contact.phones, contact.primary_phone).join(', ')}
                onChange={(value) =>
                  update(index, {
                    phones: [contact.primary_phone?.trim() ?? '', ...splitList(value)].filter(
                      Boolean,
                    ),
                  })
                }
                placeholder="Comma separated"
              />
              <TextField
                id={id('department')}
                label="Department"
                value={contact.department ?? ''}
                onChange={(value) => update(index, { department: value })}
              />
              <Field>
                <FieldLabel htmlFor={id('channel')}>Reach them by</FieldLabel>
                <EnumSelect
                  value={contact.preferred_channel}
                  onValueChange={(value) =>
                    update(index, { preferred_channel: value as HelmCommunicationChannel })
                  }
                  options={options(CHANNELS)}
                  triggerProps={{ id: id('channel') }}
                />
              </Field>
              <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={contact.is_primary}
                    onCheckedChange={() => makeMain(index)}
                    aria-label={`Make ${contact.name.trim() || `contact ${index + 1}`} the main contact`}
                  />
                  Main contact
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={contact.portal_access}
                    onCheckedChange={(checked) =>
                      update(index, { portal_access: checked === true })
                    }
                  />
                  Portal access
                </label>
              </div>
              <TextField
                id={id('notes')}
                label="Notes"
                className="sm:col-span-2"
                value={contact.notes ?? ''}
                onChange={(value) => update(index, { notes: value })}
              />
            </div>
          </FieldSet>
        )
      })}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...rows, blankContact(rows.length === 0)])}
        >
          <PlusCircle />
          Add contact
        </Button>
      </div>
    </FieldGroup>
  )
}

/**
 * The customer's addresses: add, edit, remove. At most one is the default for
 * billing and one for shipping.
 */
export function AddressesEditor({
  rows,
  onChange,
  errors,
  onClearError,
}: ListEditorProps<AddressDraft>) {
  const update = (index: number, patch: Partial<AddressDraft>, field?: string) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)))
    if (field) onClearError(`address-${index}-${field}`)
  }
  const setDefault = (
    index: number,
    flag: 'is_default_billing' | 'is_default_shipping',
    on: boolean,
  ) =>
    onChange(
      rows.map((row, rowIndex) => ({
        ...row,
        [flag]: rowIndex === index ? on : on ? false : row[flag],
      })),
    )

  return (
    <FieldGroup>
      {rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No customer addresses recorded.</p>
      )}
      {rows.map((address, index) => {
        const id = (field: string) => `address-${index}-${field}`
        return (
          <FieldSet key={index} className="rounded-md border p-3" data-testid={`address-${index}`}>
            <div className="flex items-center justify-between gap-3">
              <FieldLegend variant="label" className="mb-0">
                {address.label.trim() || `Address ${index + 1}`}
              </FieldLegend>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => onChange(rows.filter((_row, rowIndex) => rowIndex !== index))}
                aria-label={`Remove address ${index + 1}`}
              >
                <Trash2 />
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor={id('type')}>Type</FieldLabel>
                <EnumSelect
                  value={address.type}
                  onValueChange={(value) =>
                    update(index, {
                      type: value as CustomerAddressType,
                      label:
                        !address.label.trim() || address.label === formatLabel(address.type)
                          ? formatLabel(value)
                          : address.label,
                    })
                  }
                  options={options(ADDRESS_TYPES)}
                  triggerProps={{ id: id('type') }}
                />
              </Field>
              <TextField
                id={id('label')}
                label="Label"
                required
                value={address.label}
                onChange={(value) => update(index, { label: value }, 'label')}
                error={errors[id('label')]}
                placeholder="Kampala warehouse"
              />
              <TextAreaField
                id={id('address')}
                label="Address"
                className="sm:col-span-2"
                rows={3}
                value={address.raw_address ?? ''}
                onChange={(value) => update(index, { raw_address: value })}
                placeholder={'Plot 12, Jinja Road\nKampala, Uganda'}
              />
              <TextField
                id={id('city')}
                label="City"
                value={address.city ?? ''}
                onChange={(value) => update(index, { city: value })}
              />
              <TextField
                id={id('country')}
                label="Country"
                value={address.country ?? ''}
                onChange={(value) => update(index, { country: value })}
              />
              <TextField
                id={id('recipient')}
                label="Recipient"
                value={address.recipient_name ?? ''}
                onChange={(value) => update(index, { recipient_name: value })}
              />
              <TextField
                id={id('attention')}
                label="Attention of"
                value={address.attention_to ?? ''}
                onChange={(value) => update(index, { attention_to: value })}
              />
              <TextField
                id={id('phone')}
                label="Phone"
                type="tel"
                value={address.phone ?? ''}
                onChange={(value) => update(index, { phone: value })}
              />
              <TextField
                id={id('email')}
                label="Email"
                inputMode="email"
                value={address.email ?? ''}
                onChange={(value) => update(index, { email: value }, 'email')}
                error={errors[id('email')]}
              />
              <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={address.is_default_billing}
                    onCheckedChange={(checked) =>
                      setDefault(index, 'is_default_billing', checked === true)
                    }
                  />
                  Default for billing
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={address.is_default_shipping}
                    onCheckedChange={(checked) =>
                      setDefault(index, 'is_default_shipping', checked === true)
                    }
                  />
                  Default for shipping
                </label>
              </div>
            </div>
          </FieldSet>
        )
      })}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...rows, blankAddress('SHIPPING')])}
        >
          <PlusCircle />
          Add address
        </Button>
      </div>
    </FieldGroup>
  )
}
